import { useState } from 'react';

import { Grid } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { FormikProvider, useFormik } from 'formik';

import { DownloadModeloArquivoBtn } from 'components/buttons';
import { BbarForm } from 'components/docked/BbarForm';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import {
    ConfigPedidoHeaderActions,
    ConfigPedidoImportacaoUploadBtn,
    ConfigPedidoImportarEstoqueGrid,
    ConfigPedidoProdutosModal,
} from 'pages/compra/configuracao-pedido/components/';
import { useLoaderContext } from 'context/LoaderContext';
import { useConfigPedidoDirty, useMutateImportarEstoque, useQueryImportarEstoque } from 'pages/compra/configuracao-pedido/hooks/';
import { ConfigPedidoListProvider } from 'pages/compra/configuracao-pedido/contexts';
import { ProdutoEstoque, ProdutosProps } from 'pages/compra/configuracao-pedido/types';

export function ConfigPedidoImportarEstoqueForm() {
    const { data, isFetching } = useQueryImportarEstoque();
    const { onSubmit, isLoading } = useMutateImportarEstoque({ initialData: data?.configPedidoImportarEstoque || [] });

    const [openProdutoModal, setOpenProdutoModal] = useState<boolean>(false);

    useLoaderContext(isFetching || isLoading);

    const formik = useFormik({
        initialValues: data || { configPedidoImportarEstoque: [] },
        onSubmit,
        enableReinitialize: true,
    });

    const { values, setFieldValue, submitForm, dirty } = formik;

    useConfigPedidoDirty(dirty);

    /**
     * Remove os produtos selecionados da lista de produtos do pedido.
     */
    const removeProducts = (idList: Set<GridRowId>) => {
        setFieldValue(
            'configPedidoImportarEstoque',
            values.configPedidoImportarEstoque.filter((p) => !idList.has(p.idProduto)),
        );
    };

    /**
     * Adiciona os produtos selecionados na lista de produtos do pedido.
     */
    const addProducts = (products: ProdutosProps[]) => {
        const existingIds = new Set(values.configPedidoImportarEstoque.map((p) => p.idProduto));

        const newProducts: ProdutoEstoque[] = products
            .filter((p) => !existingIds.has(p.idProduto))
            .map((p) => ({
                idProduto: p.idProduto,
                cdProduto: String(p.cdProduto),
                dsProduto: p.dsProduto,
                fgAtivo: p.fgAtivo,
                fgFracionavel: p.fgFracionavel,
                qtEstoqueAtual: 0,
                qtEstoqueTotal: 0,
            }));

        setFieldValue('configPedidoImportarEstoque', [...values.configPedidoImportarEstoque, ...newProducts]);
    };

    return (
        <FormikProvider value={formik}>
            <ConfigPedidoProdutosModal open={openProdutoModal} setOpen={setOpenProdutoModal} handleAdicionarProdutos={addProducts} />

            <Block>
                <SubTitle label="Importar estoque" />

                <Grid container sx={{ justifyContent: 'space-between', gap: 2, width: '100%' }}>
                    <Grid>
                        <ConfigPedidoImportacaoUploadBtn
                            contentListName="estoqueArquivo"
                            url="gestao/configpedidoprodutoestoque/upload/"
                            onSuccessUploadFile={(values) => setFieldValue('configPedidoImportarEstoque', values)}
                        />
                    </Grid>

                    <Grid>
                        <DownloadModeloArquivoBtn dsChave="URL_ARQUIVO_MODELO_ESTOQUE" />
                    </Grid>
                </Grid>
            </Block>

            <ConfigPedidoListProvider
                onAdd={() => setOpenProdutoModal(true)}
                onRemove={removeProducts}
                searchPlaceholder="Código ou nome do produto..."
            >
                <Block>
                    <SubTitle label="Produtos" />

                    <ConfigPedidoHeaderActions />

                    <ConfigPedidoImportarEstoqueGrid handleExcluir={removeProducts} />
                </Block>
            </ConfigPedidoListProvider>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
